import { $fetch } from 'ofetch'
import { jwtDecode } from 'jwt-decode'
import { v4 as uuidv4 } from 'uuid'
import {
  JWT_ACCESS_TOKEN,
  ONE_YEAR_DURATION,
  REFRESH_TOKEN,
} from '~/src/constants/common'
import { logError } from '~/utils/log-error'
import type { TCommon } from '~/src/types/common'

// Constants for JWT validation and timeout
const JWT_REGEX = /^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/
const TOKEN_REFRESH_TIMEOUT = 10000 // 10 seconds
const MAX_REFRESH_ATTEMPTS = 2
const TOKEN_EXPIRY_BUFFER = 30000 // 30 seconds buffer before expiry

// Track refresh attempts to prevent loops
const refreshAttempts = new Map<string, number>()

/**
 * Validates JWT token format
 */
const isValidJwtFormat = (token: string): boolean => {
  if (!token || typeof token !== 'string') return false
  return JWT_REGEX.test(token)
}

/**
 * Adds an Authorization header to the option object if a token is provided
 *
 * @param options - The options object to add the header to
 * @param token - The JWT token to add to the header
 * @returns The options object with the Authorization header added (if a token was provided)
 */
const addAuthorizationHeader = (options: TCommon, token?: string) => {
  // If no token is provided or invalid format, return the options unchanged
  if (!token || !isValidJwtFormat(token)) return options

  // Add the Authorization header with the Bearer token
  options.headers.Authorization = `Bearer ${token}`

  // Return the updated options
  return options
}

export const decodeToken = (token?: string) => {
  // Initialize decoded token as null
  let decoded = null

  // Only attempt to decode if a token is provided and valid format
  if (token && isValidJwtFormat(token)) {
    try {
      // Attempt to decode the token
      decoded = jwtDecode(token)
    } catch (error) {
      // Log any errors that occur during decoding
      logError(new Error(`Failed to decode JWT: ${error.message}`))
      // Keep decoded as null if decoding fails
    }
  } else if (token) {
    // Log if token format is invalid
    logError(new Error(`Invalid JWT format: ${token.substring(0, 20)}...`))
  }

  // Return the decoded token or null if decoding failed
  return decoded
}

export const isTokenValid = ($cookies: TCommon) => {
  // Get the JWT token from cookies
  const jwtToken = $cookies.get(JWT_ACCESS_TOKEN)

  // If no token is available, or it's empty, it's not valid
  if (!jwtToken || jwtToken === '') return false

  // Validate token format first
  if (!isValidJwtFormat(jwtToken)) return false

  // Try to decode the token
  const decodedToken = decodeToken(jwtToken)

  // If the token can't be decoded, it's not valid
  if (!decodedToken) return false

  // Check if the token has expired with buffer time
  // Token is valid if the current time is less than the expiration time minus buffer
  return Date.now() < decodedToken.exp * 1000 - TOKEN_EXPIRY_BUFFER
}

export const getNewToken = async ({ $cookies, $config }: TCommon) => {
  // Get the refresh token from cookies
  const refreshToken = $cookies.get(REFRESH_TOKEN)

  // If no refresh token is available, we can't get a new token
  if (!refreshToken || refreshToken === '') {
    logError(new Error('No refresh token available'))
    return null
  }

  // Check refresh attempts to prevent loops
  const attempts = refreshAttempts.get(refreshToken) || 0
  if (attempts >= MAX_REFRESH_ATTEMPTS) {
    logError(
      new Error(`Max refresh attempts (${MAX_REFRESH_ATTEMPTS}) exceeded`),
    )
    refreshAttempts.delete(refreshToken)
    return null
  }

  // Increment attempt counter
  refreshAttempts.set(refreshToken, attempts + 1)

  try {
    // Create AbortController for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(
      () => controller.abort(),
      TOKEN_REFRESH_TIMEOUT,
    )

    // Make the API call to refresh the token with timeout
    const fetchResult = await $fetch($config.public.graphqlEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      signal: controller.signal,
      body: JSON.stringify({
        query: `
          mutation refreshToken {
            refreshJwtAuthToken(input: { clientMutationId: "${uuidv4()}", jwtRefreshToken: "${refreshToken}" }) {
              authToken
            }
          }
        `,
      }),
    }).finally(() => clearTimeout(timeoutId))

    // Check if the response has the expected structure
    if (!fetchResult?.data?.refreshJwtAuthToken?.authToken) {
      throw new Error('Invalid refresh token response structure')
    }

    // Extract the new auth token
    const authToken = fetchResult.data.refreshJwtAuthToken.authToken

    // Validate the new token format
    if (!isValidJwtFormat(authToken)) {
      throw new Error('Received invalid JWT format from refresh')
    }

    // Store the new token in cookies
    $cookies.set(JWT_ACCESS_TOKEN, authToken, {
      expires: ONE_YEAR_DURATION,
      path: '/',
    })

    // Clear refresh attempts on success
    refreshAttempts.delete(refreshToken)

    // Return the new token
    return authToken
  } catch (error: any) {
    // Check if it was a timeout
    if (error.name === 'AbortError') {
      logError(new Error('Token refresh timeout after 10 seconds'))
    } else {
      logError(error)
    }

    // Clear attempts if it's a permanent error (not a network issue)
    if (error.response?.status >= 400 && error.response?.status < 500) {
      refreshAttempts.delete(refreshToken)
    }

    return null
  }
}

export const getToken = async ({ $cookies, $config }: TCommon) => {
  // First, check if the current token is valid
  if (isTokenValid($cookies)) {
    const token = $cookies.get(JWT_ACCESS_TOKEN)
    // Extra validation before returning
    if (isValidJwtFormat(token)) {
      return token
    }
  }

  // If not valid, try to get a new token
  // Return the new token if available, otherwise return null
  return getNewToken({ $cookies, $config })
}

/**
 * Add an Authorization header if it is necessary
 *
 * @param {*} options
 * @param $cookies
 * @param $config
 * @returns
 */
export const extendOptions = async (
  options: TCommon,
  $cookies: TCommon,
  $config: TCommon,
) => {
  // Get the token (either an existing valid token or a new one)
  const jwtToken = await getToken({ $cookies, $config })

  // Add the authorization header if a token is available
  return addAuthorizationHeader(options, jwtToken)
}
